import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message as antMessage,
} from "antd";
import { CopyOutlined, PlusOutlined } from "@ant-design/icons";
import { useAuth } from "shared/contexts/AuthProvider";
import { orgStoragesService } from "../services/orgStorageService";
import { StorageEditModal } from "./StorageEditModal";
import { ReplicationRulesCard } from "./ReplicationRulesCard";
import { ReplicationAgentsCard } from "./ReplicationAgentsCard";
import { BUTTON_STYLES } from "../constants";

const { Text } = Typography;

/**
 * Extract a readable message from an axios/FastAPI error.
 */
export const getErrorDetail = (error) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  }
  if (detail && typeof detail === "object") {
    return detail.message || detail.error_code || JSON.stringify(detail);
  }
  return error?.message || "Unknown error";
};

/**
 * True when the error is a 409 STORAGE_IN_USE guard (live replicas or
 * replication rules still reference the storage).
 */
const isInUseError = (error) =>
  error?.response?.status === 409 &&
  error?.response?.data?.detail?.error_code === "STORAGE_IN_USE";

/**
 * Self-contained section for managing an organization's MCAP storages
 * (raasdata-service, multi-storage registry): a table of storages with
 * add/edit/delete, upload-target selection, connectivity tests, plus the
 * organization's replication rules and replication-agent keys.
 *
 * @param {string} orgId - Organization ID
 * @param {boolean} canManage - When false, everything is read-only
 * @param {Function} translate - Translation function (i18n key -> string)
 */
export const OrgStorageSection = ({
  orgId,
  canManage = false,
  translate = (key) => key,
}) => {
  const { authFetch } = useAuth();

  const [storages, setStorages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingStorage, setEditingStorage] = useState(null);
  const [testingId, setTestingId] = useState(null);

  const loadStorages = useCallback(async () => {
    try {
      setLoading(true);
      setForbidden(false);
      const response = await orgStoragesService.list(orgId, authFetch);
      setStorages(response.data || []);
    } catch (error) {
      if (error?.response?.status === 403) {
        setForbidden(true);
      } else {
        antMessage.error(
          translate("organizations.storages.loadFailed") +
            getErrorDetail(error),
        );
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, authFetch]);

  useEffect(() => {
    if (!orgId) return;
    loadStorages();
  }, [orgId, loadStorages]);

  const openCreate = () => {
    setEditingStorage(null);
    setEditorOpen(true);
  };

  const openEdit = (storage) => {
    setEditingStorage(storage);
    setEditorOpen(true);
  };

  const handleTest = async (storage) => {
    try {
      setTestingId(storage.storage_id);
      const response = await orgStoragesService.testConnection(
        orgId,
        storage.storage_id,
        authFetch,
      );
      const result = response.data;
      if (!result.testable) {
        antMessage.info(
          result.detail || translate("organizations.storage.notTestable"),
        );
      } else if (result.ok) {
        antMessage.success(
          result.detail || translate("organizations.storage.testSuccess"),
        );
      } else {
        antMessage.error(
          translate("organizations.storage.testFailed") + (result.detail || ""),
        );
      }
    } catch (error) {
      antMessage.error(
        translate("organizations.storage.testFailed") + getErrorDetail(error),
      );
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (storage, force = false) => {
    try {
      await orgStoragesService.remove(
        orgId,
        storage.storage_id,
        authFetch,
        force,
      );
      antMessage.success(translate("organizations.storages.deleteSuccess"));
      loadStorages();
    } catch (error) {
      if (!force && isInUseError(error)) {
        const detail = error.response.data.detail;
        Modal.confirm({
          title: translate("organizations.storages.inUseTitle"),
          content: `${translate("organizations.storages.inUseWarning")} (replicas: ${detail.replica_count}, rules: ${detail.rule_count})`,
          okText: translate("organizations.storages.forceDelete"),
          okButtonProps: { danger: true },
          onOk: () => handleDelete(storage, true),
        });
        return;
      }
      antMessage.error(
        translate("organizations.storages.deleteFailed") +
          getErrorDetail(error),
      );
    }
  };

  const handleSetUploadTarget = async (storage) => {
    try {
      await orgStoragesService.setUploadTarget(
        orgId,
        storage.storage_id,
        authFetch,
      );
      antMessage.success(
        translate("organizations.storages.uploadTargetChanged"),
      );
      loadStorages();
    } catch (error) {
      antMessage.error(
        translate("organizations.storages.uploadTargetFailed") +
          getErrorDetail(error),
      );
    }
  };

  const handleClearUploadTarget = async () => {
    try {
      await orgStoragesService.clearUploadTarget(orgId, authFetch);
      antMessage.success(
        translate("organizations.storages.uploadTargetChanged"),
      );
      loadStorages();
    } catch (error) {
      antMessage.error(
        translate("organizations.storages.uploadTargetFailed") +
          getErrorDetail(error),
      );
    }
  };

  if (!orgId) return null;

  const hasOrgTarget = storages.some(
    (s) => s.is_upload_target && s.enabled && s.storage_class === "org",
  );

  const columns = [
    {
      title: translate("organizations.storages.name"),
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Space direction="vertical" size={0}>
           <Text strong
            copyable={{
              text: record.storage_id,
              icon: <CopyOutlined style={{ fontSize: 11 }} />,
              tooltips: [
                translate("organizations.storages.copyId"),
                translate("organizations.storages.idCopied"),
              ],
            }}
          > 
            {name} 
            <Text type="secondary" style={{ fontSize: 11, fontFamily: "monospace" }}>
              {`(${translate("organizations.storages.id")}: ${record.storage_id})`}
            </Text>
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.config?.endpoint
              ? `${record.config.endpoint}${record.config.bucket ? " / " + record.config.bucket : ""}`
              : record.config?.base_path || ""}
          </Text>
        </Space>
      ),
    },
    {
      title: translate("organizations.storages.kind"),
      key: "kind",
      render: (_, record) => (
        <Space size={4} wrap>
          <Tag>{record.storage_type}</Tag>
          {record.storage_class === "system" ? (
            <Tag color="geekblue">
              {translate("organizations.storages.system")}
            </Tag>
          ) : record.managed ? (
            <Tag color="green">{translate("organizations.storages.managed")}</Tag>
          ) : (
            <Tag color="orange">
              {translate("organizations.storages.unmanaged")}
            </Tag>
          )}
          {!record.enabled && (
            <Tag color="red">{translate("organizations.storages.disabled")}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: translate("organizations.storages.uploadTarget"),
      key: "upload_target",
      align: "center",
      render: (_, record) => {
        // The system storage is the implicit fallback target.
        const checked =
          record.storage_class === "system"
            ? !hasOrgTarget
            : record.is_upload_target && record.enabled;
        return (
          <Radio
            checked={checked}
            disabled={
              !canManage || (!record.enabled && record.storage_class !== "system")
            }
            onChange={() =>
              record.storage_class === "system"
                ? handleClearUploadTarget()
                : handleSetUploadTarget(record)
            }
          />
        );
      },
    },
    {
      title: translate("organizations.storages.actions"),
      key: "actions",
      render: (_, record) => (
        <Space size={4} wrap>
          <Button
            size="small"
            loading={testingId === record.storage_id}
            disabled={!record.managed}     
            style={BUTTON_STYLES.add}       
            onClick={() => handleTest(record)}
          >
            {translate("organizations.storage.testConnection")}
          </Button>
          {canManage && record.storage_class === "org" && (
            <>
              <Button size="small" onClick={() => openEdit(record)}>
                {translate("organizations.storages.edit")}
              </Button>
              <Popconfirm
                title={translate("organizations.storages.deleteConfirm")}
                onConfirm={() => handleDelete(record)}
              >
                <Button size="small" danger>
                  {translate("organizations.storages.delete")}
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Text strong>{translate("organizations.storage.title")}</Text>
      <Text
        type="secondary"
        style={{ display: "block", marginBottom: 8, fontSize: 12 }}
      >
        {translate("organizations.storages.subtitle")}
      </Text>

      {forbidden ? (
        <Alert
          type="warning"
          showIcon
          message={translate("organizations.storage.noPermission")}
        />
      ) : loading ? (
        <div style={{ textAlign: "center", padding: 16 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Table
            size="small"
            rowKey="storage_id"
            columns={columns}
            dataSource={storages}
            pagination={false}
            style={{ marginBottom: 12 }}
          />
          {canManage && (
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={openCreate}
              style={{ ...BUTTON_STYLES.add, marginBottom: 16 }}
            >
              {translate("organizations.storages.add")}
            </Button>
          )}

          <ReplicationRulesCard
            orgId={orgId}
            storages={storages}
            canManage={canManage}
            translate={translate}
            onChanged={loadStorages}
          />

          {canManage && (
            <ReplicationAgentsCard
              orgId={orgId}
              storages={storages}
              translate={translate}
            />
          )}
        </>
      )}

      <StorageEditModal
        open={editorOpen}
        orgId={orgId}
        storage={editingStorage}
        translate={translate}
        onClose={() => setEditorOpen(false)}
        onSaved={() => {
          setEditorOpen(false);
          loadStorages();
        }}
      />
    </div>
  );
};
